import React, { useEffect, Suspense } from 'react';
import { Switch, withRouter, Route } from 'react-router-dom';
import { compose, prop } from 'ramda';
import { AsyncBookingList } from 'pages/async';
import { usePageVisibility } from 'react-page-visibility';
import { getAppRoutes } from 'routing';
import { Layout } from 'components';
import { useScrollToTop, useEffectBoundary } from 'effects';
import { withAuthentication, withUser } from 'hoc';
import connect from './App.state';
import { FastSearchContainerConnected } from 'containers/FastSearch';
import { RatesImportContainerConnected } from 'containers/RatesImport';
import { ProductsImportContainerConnected } from 'containers/ProductsImport';
import { StaticRatesAvailabilityImportContainerConnected } from 'containers/StaticRatesAvailabilityImport';
import { OfferRouting } from 'containers/OfferRouting';
import { AnnouncementsRouting } from 'containers/Announcements';
import { HotelCollectionRouting } from 'containers/HotelCollection/routing';
import { BookingManagerRouting } from 'containers/BookingManager/routing';
import AuthRoute from 'containers/AuthRoute';
import { EUserType } from 'services/BackendApi';
import { ENVIRONMENT, COGNITO_ENABLE_FLOW, APP_ROUTING_PATHS } from 'config';
import { RateBreakRouting } from 'containers/RateBreak/routing';
import { TravelCompaniesRouting } from 'containers/TravelCompanies/routing';
import { TravelAgentsRouting } from 'containers/TravelAgents/routing';
import { InternalUsersRouting } from 'containers/InternalUsers/routing';
import { StatementRouting } from 'containers/Statement';
import { BookingBuilderV1 } from 'pages/BookingBuilderV1';
import { ProposalsRouting } from 'containers/Proposals/routing';
import { CredentialsInfoRouting } from 'containers/CredentialsInfo';
import { Dashboard } from 'pages/Dashboard/Dashboard';
import Landing from 'containers/Landing/Landing';
import { InventoryWorkspacesHomepage } from 'containers/InventoryWorkspacesHomepage';
import { LiveRatesRouting } from 'containers/LiveRates/routing';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { CognitoCallback } from 'pages/CognitoCallback/CognitoCallback';
import { CognitoFlowError } from 'pages/CognitoFlowError/CognitoFlowError';
import { NotFound } from 'pages/NotFound/NotFound';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { useSelector } from 'react-redux';
import { getBootstrapErrorSelector } from 'store/modules/bootstrap/selectors';
import { AncillarySuppliersRouting } from 'containers/AncillarySuppliers/routing';
import { AncillaryActivitiesRouting } from 'containers/AncillaryActivities/routing';
import { AncillaryTransfersRouting } from 'containers/AncillaryTransfers/routing';
import { AncillaryGroundServicesRouting } from 'containers/AncillaryGroundServices/routing';
import { BookingConfirmationRouting } from 'containers/BookingConfirmation/routing';
import { TaskManagementRouting } from 'containers/TaskManagement/routing';
import { BasketRouting } from 'containers/Basket/routing';
import { HotelAdminRouting } from 'containers/HotelAdmin/routing';
import { getBearerTokenFromLocalStorage } from 'services/tokenLocalStorage';
import { CRMRouting } from 'containers/CRM/routing';
import { ContentsAdminRouting } from 'containers/ContentsAdmin/routing';
import { ContentMarketingRoutingWithRouter } from 'containers/ContentMarketing/routing';
import { noop } from 'utils';
import { Settings } from 'pages/Settings/Settings';
import Notifications from 'components/Notifications';
import { SnackbarProvider } from 'notistack';
const ANCHOR_ORIGIN = Object.freeze({
  vertical: 'bottom',
  horizontal: 'left',
});

import { SharedLinkRouting } from 'containers/SharedLinkPage/routing';
import { OtpRouting } from 'containers/Otp/routing';
import { ErrorBoundary } from 'ui/ErrorBoundary/ErrorBoundary';
import { Loader } from 'components/Loader/Loader';
import { ItineraryPageRouting } from 'containers/Itinerary/routing';
import withDynamicParameters from 'hoc/withDynamicParameters/withDynamicParameters';
import { VoucherPageRouting } from 'containers/Voucher/routing';
import { ChatPageRouting } from 'containers/Chat/routing';
import { ReviewPageRouting } from 'containers/ReviewPage/routing';
import { KnowledgeBaseRouting } from 'containers/KnowledgeBase/routing';
import { VccRouting } from 'containers/Vcc';
import { BankTransactionsRouting } from 'containers/BankTransactions/routing';
import { ConversionRatesPage } from 'containers/ConversionRates/ConversionRatesPage';
import { SuppliersReconciliationPage } from 'containers/SuppliersReconciliation/SuppliersReconciliationPage';
import { PurchaseOrderDetailPage } from 'containers/SuppliersReconciliation/PurchaseOrderDetailPage';
import { InvoiceUploadsPage } from 'containers/InvoiceUploads';
import { RechartsDemo } from 'pages/RechartsDemo';

export const App = ({
  location: { pathname },
  user,
  resetStatuses,
  pageChange,
  bootstrapInitRequestAction,
  bootstrapAppRequestAction,
  visibilityChange,
  isAppVersionDeprecated = false,
  isAuthenticated,
  setToken = noop,
}) => {
  const { dynamicParameters, dynamicParametersAreEmpty } = useDynamicParameters();
  const bootstrapError = useSelector(getBootstrapErrorSelector);

  // Scroll to top on path change
  useScrollToTop(pathname);

  useEffect(() => {
    // App can load at the beginning or at any point of the flow
    // Either user is logged in and can throw BOOTSTRAP_APP_REQUEST
    // Either is not yet then when AUTH_LOG_IN_SUCCESS happens same saga will be triggered
    const token = getBearerTokenFromLocalStorage();
    if (!!token.value && !token.expired) {
      // Normal bootstrap flow for app load at any point
      bootstrapAppRequestAction();
    } else {
      // First Cognito flow, then normal bootstrap flow
      bootstrapInitRequestAction();
    }
  }, []);

  useEffectBoundary(() => {
    pageChange(pathname);

    // Reset statuses on page change
    resetStatuses();
  }, [pathname]);

  const visible = usePageVisibility();

  useEffect(() => {
    visibilityChange(visible);
  }, [visibilityChange, visible]);

  if (bootstrapError) {
    return (
      <Layout isAppVersionDeprecated={isAppVersionDeprecated}>
        <div className="bootstrap-error mt-[100px]">
          <ErrorBar message="Service temporary unavailable. Please try again later." />
        </div>
      </Layout>
    );
  }

  if (dynamicParametersAreEmpty) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <LoadingBar />
      </div>
    );
  }

  if (
    ENVIRONMENT !== 'local' &&
    !isAuthenticated &&
    window.location.pathname === '/' &&
    dynamicParameters.MARKETING_APP_URL
  ) {
    window.location.replace(dynamicParameters.MARKETING_APP_URL);
  }

  return (
    <Layout isAppVersionDeprecated={isAppVersionDeprecated}>
      <Suspense fallback={<Loader />}>
        <Switch>
          {/* If inventory is ON, all users get the new homepage... */}
          <AuthRoute
            path="/"
            exact
            component={InventoryWorkspacesHomepage}
            allow={[
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.RL,
              EUserType.SR,
              EUserType.TA,
              EUserType.SALES,
              EUserType.MARKETING,
            ]}
          />
          {/* ...and everyone gets a new "filters" page */}
          <AuthRoute
            path="/filters"
            exact
            component={user && (user.type === EUserType.SR || user.type === EUserType.ADMIN) ? Dashboard : Landing}
            allow={[EUserType.TA, EUserType.ADMIN, EUserType.FINANCE, EUserType.RL, EUserType.SR]}
          />

          <AuthRoute
            path="/search/beta"
            exact
            component={FastSearchContainerConnected}
            allow={[EUserType.TA, EUserType.SR, EUserType.ADMIN]}
          />
          <AuthRoute path="/booking-manager" component={BookingManagerRouting} />
          <AuthRoute allow={[EUserType.RL, EUserType.ADMIN]} path="/offers" component={OfferRouting} />
          <AuthRoute
            allow={[EUserType.RL, EUserType.ADMIN, EUserType.FINANCE]}
            path="/announcements"
            component={AnnouncementsRouting}
          />
          <AuthRoute
            allow={[
              EUserType.RL,
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.SALES,
              EUserType.SR,
              EUserType.TA,
              EUserType.MARKETING,
            ]}
            path="/hotels-collection"
            component={HotelCollectionRouting}
          />
          <AuthRoute
            allow={[EUserType.RL, EUserType.ADMIN]}
            exact
            path="/rates/import"
            component={RatesImportContainerConnected}
          />
          <AuthRoute
            allow={[EUserType.RL, EUserType.ADMIN]}
            exact
            path="/products/import"
            component={ProductsImportContainerConnected}
          />
          <AuthRoute
            allow={[EUserType.RL, EUserType.ADMIN]}
            exact
            path="/static-rates-availability/import"
            component={StaticRatesAvailabilityImportContainerConnected}
          />
          <AuthRoute
            allow={[EUserType.TA, EUserType.SR, EUserType.RL, EUserType.FINANCE, EUserType.ADMIN, EUserType.SALES]}
            exact
            path="/bookings"
            component={AsyncBookingList}
          />
          <AuthRoute allow={[EUserType.RL, EUserType.ADMIN]} path="/rate-breaks" component={RateBreakRouting} />
          {dynamicParameters.ENABLE_ANCILLARY_SUPPLIERS_IMPORT && !dynamicParameters.ENABLE_CRM_WORKSPACE_TEMP && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/suppliers"
              component={AncillarySuppliersRouting}
            />
          )}
          {dynamicParameters.ENABLE_ANCILLARY_ACTIVITIES_IMPORT && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/activities"
              component={AncillaryActivitiesRouting}
            />
          )}
          {dynamicParameters.ENABLE_ANCILLARY_TRANSFERS_IMPORT && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/transfers"
              component={AncillaryTransfersRouting}
            />
          )}
          {dynamicParameters.ENABLE_ANCILLARY_GROUND_SERVICES_IMPORT && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/ground-services"
              component={AncillaryGroundServicesRouting}
            />
          )}
          {/* Admin, SR, Finance --> Can see one TC's settings, but SR and F cannot see the list for all TCs */}
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR, EUserType.FINANCE]}
            path="/travel-companies"
            component={TravelCompaniesRouting}
          />
          <AuthRoute allow={[EUserType.ADMIN]} path="/users" component={InternalUsersRouting} />
          <AuthRoute allow={[EUserType.ADMIN, EUserType.SR]} path="/travel-agents" component={TravelAgentsRouting} />
          <AuthRoute allow={[EUserType.ADMIN]} path="/users" component={InternalUsersRouting} />
          <AuthRoute allow={[EUserType.ADMIN, EUserType.FINANCE]} path="/vcc" component={VccRouting} />
          {dynamicParameters.ENABLE_BANK_TRANSACTIONS_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR]}
              path="/finance/bank-transactions"
              component={BankTransactionsRouting}
            />
          )}
          {dynamicParameters.ENABLE_FX_MATRIX_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.RL]}
              path="/finance/conversion-rates"
              component={ConversionRatesPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/suppliers-reconciliation/detail"
              component={PurchaseOrderDetailPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/suppliers-reconciliation"
              component={SuppliersReconciliationPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/invoice-uploads"
              component={InvoiceUploadsPage}
            />
          )}
          <AuthRoute
            allow={
              user?.companyTaManager || user?.taCanReadFinance
                ? [EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.TA]
                : [EUserType.ADMIN, EUserType.FINANCE, EUserType.SR]
            }
            path="/statement"
            component={StatementRouting}
          />
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
            path="/booking-builder"
            component={BookingBuilderV1}
          />
          <AuthRoute
            allow={[EUserType.TA, EUserType.SR, EUserType.ADMIN]}
            path="/proposals-v2"
            component={ProposalsRouting}
          />
          {dynamicParameters.ENABLE_CHAT_NEW_PAGE_TEMP && (
            <AuthRoute
              allow={
                dynamicParameters.ENABLE_CHAT_NEW_PAGE_TA_TEMP
                  ? [EUserType.SR, EUserType.ADMIN, EUserType.TA]
                  : [EUserType.SR, EUserType.ADMIN]
              }
              path="/pure-ai"
              component={ChatPageRouting}
            />
          )}
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
            path="/booking-confirmation"
            component={BookingConfirmationRouting}
          />
          {dynamicParameters.ENABLE_BASKET && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
              path={APP_ROUTING_PATHS.basket.home}
              component={BasketRouting}
            />
          )}
          {dynamicParameters.ENABLE_BASKET && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
              path={APP_ROUTING_PATHS.basket.edit}
              component={BookingBuilderV1}
            />
          )}
          <AuthRoute allow={[EUserType.ADMIN, EUserType.SR]} path="/ta-history" component={CredentialsInfoRouting} />
          <AuthRoute allow={[EUserType.RL, EUserType.ADMIN]} path="/live-rates" component={LiveRatesRouting} />
          {COGNITO_ENABLE_FLOW && <Route path="/login-error" component={CognitoFlowError} />}
          {COGNITO_ENABLE_FLOW && <Route path="/cognito-callback" component={CognitoCallback} />}
          {COGNITO_ENABLE_FLOW && <Route path="/login" component={NotFound} />}
          {COGNITO_ENABLE_FLOW && <Route path="/settings/password" component={NotFound} />}
          {((user?.type === EUserType.TA && dynamicParameters.ENABLE_OPERATIONS_WORKSPACE_TA) ||
            (user?.type !== EUserType.TA && dynamicParameters.ENABLE_OPERATIONS_WORKSPACE_INTERNAL_USERS)) && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA, EUserType.FINANCE, EUserType.RL, EUserType.SALES]}
              path="/tasks"
              component={TaskManagementRouting}
            />
          )}

          <AuthRoute allow={[EUserType.ADMIN, EUserType.RL]} path="/hotel-admin" component={HotelAdminRouting} />

          <AuthRoute allow={[EUserType.ADMIN, EUserType.RL]} path="/content-admin" component={ContentsAdminRouting} />

          <Route path="/content" component={ContentMarketingRoutingWithRouter} />

          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.SALES]}
            path="/crm"
            component={CRMRouting}
          />

          <AuthRoute
            allow={[
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.MARKETING,
              EUserType.RL,
              EUserType.SALES,
              EUserType.SR,
              EUserType.TA,
            ]}
            path="/knowledgebase"
            component={KnowledgeBaseRouting}
          />

          {/* this does the /settings/profile-details and /settings/company-details */}
          <AuthRoute
            allow={[
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.SR,
              EUserType.SALES,
              EUserType.TA,
              EUserType.MARKETING,
            ]}
            path="/settings/:section?"
            component={Settings}
          />

          {dynamicParameters.ENABLE_BOOKING_ITINERARY_TEMP && (
            <Route path={APP_ROUTING_PATHS.itinerary} component={ItineraryPageRouting} />
          )}

          <Route path={APP_ROUTING_PATHS.reviews} component={ReviewPageRouting} />

          <Route path={APP_ROUTING_PATHS.voucher} component={VoucherPageRouting} />
          <Route path={APP_ROUTING_PATHS.sharedLink} component={SharedLinkRouting} />
          <Route path={APP_ROUTING_PATHS.otp} component={OtpRouting} />
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.SALES, EUserType.MARKETING]}
            path="/demo/recharts"
            component={RechartsDemo}
          />

          {...getAppRoutes(prop('type', user))}
        </Switch>
      </Suspense>

      <div id="portal-header"></div>
      <div id="portal-tooltip"></div>
      <div id="portal-modal"></div>
      <div id="portal-overlay"></div>
      <div id="portal-bottom-drawer"></div>
      <div id="portal-toast-notification"></div>

      <div className="z-[999999]">
        <SnackbarProvider anchorOrigin={ANCHOR_ORIGIN} className="z-[999999]">
          <Notifications />
        </SnackbarProvider>
      </div>
    </Layout>
  );
};

export default compose(ErrorBoundary, withRouter, withUser, withAuthentication, withDynamicParameters, connect)(App);
